export type IconPickerTab = 'icons' | 'fontawesome' | 'emojis';

export const ICON_PICKER_TABS: readonly IconPickerTab[] = ['fontawesome', 'icons', 'emojis'] as const;

export const ICON_PICKER_TAB_LABELS: Record<IconPickerTab, string> = {
  icons: 'Simple Icons',
  fontawesome: 'Icons',
  emojis: 'Emojis',
};

// --- Custom Image Icons ---

export interface CustomIconEntry {
  readonly label: string;
  /** Path relative to assets, e.g. 'assets/images/icons/math.png' */
  readonly src: string;
}

export interface CustomIconCategory {
  readonly label: string;
  readonly icons: readonly CustomIconEntry[];
}

export const CUSTOM_IMAGE_ICONS: readonly CustomIconCategory[] = [
  {
    label: 'General',
    icons: [{ label: 'Earth', src: 'assets/icons/simple/earth.png' }],
  },
];

export const ALL_CUSTOM_ICONS: readonly CustomIconEntry[] = CUSTOM_IMAGE_ICONS.flatMap((c) => c.icons);

// --- FontAwesome Curated Icons ---

export interface FaIconCategory {
  readonly label: string;
  readonly icons: readonly string[];
}

export const FA_ICON_COLORS = [
  { label: 'Default', value: 'currentColor' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Green', value: '#22c55e' },
  { label: 'Teal', value: '#14b8a6' },
  { label: 'Sky', value: '#0ea5e9' },
  { label: 'Indigo', value: '#6366f1' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Pink', value: '#ec4899' },
] as const;

export type FaIconColor = (typeof FA_ICON_COLORS)[number];

export const CURATED_FA_ICONS: readonly FaIconCategory[] = [
  {
    label: 'Commonly Used',
    icons: [
      'pen-to-square', // Worked Examples
      'list-check', // Methods
      'bolt', // Thunder
      'globe', // World
      'scale-balanced', // Comparison
      'clipboard-list', // Exam
      'lightbulb', // Light Bulb
      'book', // Book
      'circle-info', // Tip
      'calculator', // Calculator
      'brain', // Don't Forget / Remember
      'fire', // Fire
      'circle-exclamation', // Info / Alert
    ],
  },
  {
    label: 'Education',
    icons: [
      'graduation-cap',
      'book',
      'book-open',
      'book-bookmark',
      'chalkboard',
      'school',
      'pen',
      'pencil',
      'pen-fancy',
      'ruler',
      'spell-check',
      'language',
      'globe',
      'landmark',
    ],
  },
  {
    label: 'Science & Math',
    icons: [
      'flask',
      'flask-vial',
      'atom',
      'microscope',
      'dna',
      'vial',
      'brain',
      'calculator',
      'square-root-variable',
      'infinity',
      'magnet',
      'temperature-half',
      'biohazard',
      'radiation',
    ],
  },
  {
    label: 'Charts & Data',
    icons: [
      'chart-line',
      'chart-bar',
      'chart-pie',
      'chart-area',
      'chart-column',
      'chart-simple',
      'table',
      'database',
      'diagram-project',
      'sitemap',
    ],
  },
  {
    label: 'Objects',
    icons: [
      'lightbulb',
      'star',
      'heart',
      'flag',
      'trophy',
      'medal',
      'crown',
      'gem',
      'key',
      'lock',
      'unlock',
      'gift',
      'bookmark',
      'tag',
      'tags',
      'thumbtack',
    ],
  },
  {
    label: 'Communication',
    icons: ['comment', 'comments', 'envelope', 'bell', 'bullhorn', 'phone', 'paper-plane', 'inbox', 'at', 'hashtag'],
  },
  {
    label: 'Technology',
    icons: [
      'laptop',
      'desktop',
      'mobile-screen',
      'tablet-screen-button',
      'robot',
      'code',
      'terminal',
      'microchip',
      'wifi',
      'server',
      'cloud',
      'plug',
      'bolt',
      'gear',
    ],
  },
  {
    label: 'People & Activities',
    icons: [
      'user',
      'users',
      'people-group',
      'person-running',
      'hands-clapping',
      'hand-point-right',
      'hand-peace',
      'face-smile',
      'face-laugh',
      'face-meh',
      'face-frown',
      'face-surprise',
    ],
  },
  {
    label: 'Shapes & Symbols',
    icons: [
      'circle',
      'square',
      'play',
      'diamond',
      'check',
      'xmark',
      'plus',
      'minus',
      'equals',
      'arrow-right',
      'arrow-left',
      'arrow-up',
      'arrow-down',
      'arrows-rotate',
      'circle-check',
      'circle-xmark',
      'circle-exclamation',
      'circle-info',
      'triangle-exclamation',
    ],
  },
  {
    label: 'Nature & Weather',
    icons: [
      'sun',
      'moon',
      'cloud-sun',
      'cloud-rain',
      'snowflake',
      'fire',
      'water',
      'leaf',
      'tree',
      'seedling',
      'mountain',
      'wind',
    ],
  },
] as const;

export const ALL_CURATED_FA_ICON_NAMES: readonly string[] = CURATED_FA_ICONS.flatMap((c) => c.icons);
